import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { getApiBaseUrl } from '@/lib/capacitor';
import type { UpdateInfo } from '@/hooks/useUpdateCheck';

type BooleanResult = {
  value: boolean;
};

type DownloadResult = {
  path: string;
};

type InstallResult = {
  launched: boolean;
};

export type UpdateDownloadProgress = {
  fileName: string;
  bytesReceived: number;
  totalBytes?: number;
  progress: number;
};

type NativeUpdaterPlugin = {
  canRequestPackageInstalls(): Promise<BooleanResult>;
  openInstallSettings(): Promise<void>;
  downloadApk(options: { url: string; filename: string; headers?: Record<string, string> }): Promise<DownloadResult>;
  installDownloadedApk(options: { path: string }): Promise<InstallResult>;
  addListener(
    eventName: 'apkDownloadProgress',
    listenerFunc: (progress: UpdateDownloadProgress) => void,
  ): Promise<PluginListenerHandle>;
};

const AppUpdater = registerPlugin<NativeUpdaterPlugin>('AppUpdater', {
  web: () => import('./native-updater.web').then((module) => new module.AppUpdaterWeb()),
});

function resolveDownloadUrl(update: UpdateInfo) {
  if (/^https?:\/\//i.test(update.downloadUrl)) {
    return update.downloadUrl;
  }

  return `${getApiBaseUrl()}${update.downloadUrl}`;
}

function resolveFilename(update: UpdateInfo) {
  return update.tag
    ? `ping-${update.tag}.apk`
    : `ping-v${update.versionName}.apk`;
}

export async function installUpdateRelease(update: UpdateInfo, existingPath?: string) {
  const url = resolveDownloadUrl(update);

  if (Capacitor.getPlatform() !== 'android') {
    window.open(url, '_blank', 'noopener,noreferrer');
    return { status: 'opened-external' as const };
  }

  const path = existingPath || (await AppUpdater.downloadApk({
    url,
    filename: resolveFilename(update),
  })).path;

  const permission = await AppUpdater.canRequestPackageInstalls();
  if (!permission.value) {
    await AppUpdater.openInstallSettings();
    return { status: 'downloaded-awaiting-permission' as const, path };
  }

  await AppUpdater.installDownloadedApk({ path });
  return { status: 'install-started' as const, path };
}

export async function resumeDownloadedUpdateInstall(path: string) {
  if (Capacitor.getPlatform() !== 'android') {
    throw new Error('APK installation is only available on Android.');
  }

  const permission = await AppUpdater.canRequestPackageInstalls();
  if (!permission.value) {
    await AppUpdater.openInstallSettings();
    return { status: 'downloaded-awaiting-permission' as const, path };
  }

  await AppUpdater.installDownloadedApk({ path });
  return { status: 'install-started' as const, path };
}

export async function onUpdateDownloadProgress(listener: (progress: UpdateDownloadProgress) => void) {
  return AppUpdater.addListener('apkDownloadProgress', listener);
}
